import {
	Avatar, AvatarEntity, AvatarSize, Counter, DATE_DISPLAY_FORMAT,
	MiddleEllipsisWhisper,
} from '@aleph/shared-components';
import { FC, forwardRef } from 'react';
import dayjs from 'dayjs';
import {
	getReportClientIds,
	ReportRow,
} from 'wb-spotify/app/routes/reports/utils';
import SpotifyReportsActions from 'wb-spotify/app/routes/reports/components/actions';
import classNames from 'classnames';
import useReportActionsOpen from 'wb-spotify/app/routes/reports/hooks/useReportActionsOpen';
import { useTranslation } from 'react-i18next';
import { SpotifyReportSchedule } from 'wb-spotify/src/app/common/utils';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import {
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED,
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED,
} from 'wb-spotify/app/routes/superGrid/statistics/utils';
import { useNavigate } from 'react-router-dom';
import { HandleUpdateRow } from 'wb-spotify/app/routes/hooks/useUpdateRows';

interface IProps {
	reportItem: ReportRow & { reportImage?: string }
	handleUpdateRow: HandleUpdateRow<ReportRow>
}

const SpotifyReportListItem: FC<IProps> = forwardRef<HTMLDivElement, IProps>(({ reportItem, handleUpdateRow, ...rest }, ref) => {
	const { t } = useTranslation();
	const { lifetimeStats } = useSupergridSettings();
	const navigate = useNavigate();

	const getMetricsCategory = (metrics: (string | null)[]) => {
		const selectedCategory = (
			lifetimeStats
				? SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED
				: SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED
		).find(category => category.metrics.every(metric => metrics.includes(metric.value)));

		if (selectedCategory?.metrics?.length === metrics?.length) {
			return selectedCategory?.label;
		}

		return t('sc: chart custom', 'Custom');
	};

	const { reportActionsOpenId } = useReportActionsOpen();

	const getScheduleName = (report: ReportRow) => {
		// Change the type of reportItem to SpotifyReport
		const spotifyReport = report;

		if (spotifyReport.reportSchedule === SpotifyReportSchedule.OnlyOnce) {
			return `${dayjs(spotifyReport.reportDateRangeOptions?.dateRangeValue?.[0]).format(DATE_DISPLAY_FORMAT)} - ${dayjs(spotifyReport.reportDateRangeOptions?.dateRangeValue?.[1]).format(DATE_DISPLAY_FORMAT)}`;
		}

		if (spotifyReport.reportSchedule === SpotifyReportSchedule.EveryMonday) {
			return 'Every Monday';
		}

		if (spotifyReport.reportSchedule === SpotifyReportSchedule.EveryWeekday) {
			return 'Every Weekday';
		}

		if (spotifyReport.reportSchedule === SpotifyReportSchedule.FirstOfTheMonth) {
			return 'First of the Month';
		}

		return 'Custom';
	};

	function onReportEdit() {
		navigate(`/reports/${reportItem?.reportId}`);
	}

	return (
		<div className="border-b border-b-gray-200 whitespace-nowrap">
			<div
				key={reportItem?.reportId}
				{...rest}
				ref={ref}
				className={
					classNames(
						'flex h-[82px] transition-[opacity] group items-center text-black '
						+ 'hover:bg-gray-100 hover:opacity-100',
						{
							'opacity-40':  reportActionsOpenId && reportActionsOpenId !== reportItem?.reportId,
							'bg-gray-100': reportActionsOpenId && reportActionsOpenId === reportItem?.reportId,
						},
					)
				}
				data-testid={`reportentry-${reportItem?.reportId}`}
			>
				<div data-testid="reportsid"
					className="flex-1 px-[8px] text-[16px] gap-[16px] overflow-hidden
						group group-hover:text-blue cursor-pointer flex items-center"
					onClick={onReportEdit}
				>
					<div
						className={classNames({
							'opacity-20': !reportItem.reportReady,
						})}
						data-testid={reportItem.reportReady ? 'activated' : 'paused'}
					>
						<Avatar
							src={reportItem.reportImage}
							size={AvatarSize.M}
							entityType={AvatarEntity.Report}
						/>
					</div>
					<div className="overflow-hidden font-medium w-full">
						<MiddleEllipsisWhisper value={reportItem?.reportName} />
					</div>
				</div>
				<div className="relative w-[80px] flex items-center justify-center">
					<Counter
						count={
							getReportClientIds(reportItem?.reportFiltersOptions).length
						}
						showZero
					/>
				</div>
				<div className="px-[8px] w-[220px] text-gray-800">
					{getScheduleName(reportItem)}
				</div>
				<div className="px-[8px] w-[180px] text-gray-800">{
					getMetricsCategory(reportItem?.reportMetrics ?? [])
				}</div>
				<div className="px-[8px] w-[260px]">
					<SpotifyReportsActions
						report={reportItem}
						handleUpdateRow={handleUpdateRow}
					/>
				</div>
			</div>
		</div>
	);
});

export default SpotifyReportListItem;
